import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackException } from '../lib/appInsights';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level boundary so a render-time exception surfaces as a friendly page instead of a
 * blank screen, and gets reported to Application Insights with the originating component
 * stack. Use a more granular boundary inside complex pages if you want per-section fallback.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    trackException(error, { componentStack: info.componentStack });
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <section className="bg-white rounded-lg shadow p-6 max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-atg-navy">Something went wrong</h1>
          <p className="mt-2 text-sm text-slate-600">
            The error has been reported. Try reloading the page.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 bg-atg-blue text-white px-4 py-2 rounded hover:bg-atg-navy"
          >
            Reload
          </button>
        </section>
      </div>
    );
  }
}
